


import React from 'react';
import BackgroundVisuals from "./BackgroundVisuals";


const About = () => {
  const differentiators = [
    {
      title: "AI-First Approach",
      description: "We don't just automate processes; we engineer intelligence into the DNA of your operations.",
      image: "/images/ai_strategy.png"
    },
    {
      title: "End-to-End Delivery",
      description: "From architectural strategy to deployment and governance, we own the complete lifecycle.",
      image: "/images/packaging.png"
    },
    {
      title: "Enterprise-Grade Security",
      description: "Built-in compliance and security protocols across every neural implementation.",
      image: "/images/cyber-security.png"
    },
    {
      title: "Speed & Scale",
      description: "Combining rapid prototyping with mature engineering and industrial-grade quality.",
      image: "/images/connection.png"
    }
  ];

  const credentials = [
    { icon: "🎓", title: "Elite Education", description: "BITS and IIM alumni with advanced degrees in AI and Management." },
    { icon: "🏆", title: "Patented Tech", description: "Proprietary AI solutions with patents in cutting-edge industrial applications." },
    { icon: "🌍", title: "Global Tenure", description: "70+ years of combined international experience across major tech continents." },
    { icon: "🚀", title: "Proven ROI", description: "Fortune 500 to high-growth startups—delivered $1B+ in revenue." },
    { icon: "🎯", title: "Deep Expertise", description: "Multi-dimensional AI mastery: Vision, NLP, Multi-modal, and Generative AI." },
    { icon: "👔", title: "Global Leadership", description: "Ex-leadership roles at global tech giants and disruptive AI innovators." }
  ];

  return (
    <div className="pt-24 min-h-screen bg-white relative selection:bg-[#952877]/10 selection:text-[#952877]">
      <BackgroundVisuals />
      
      {/* Decorative Brand Glows */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-0 w-[500px] h-[500px] bg-[#592aa7]/5 blur-[100px] rounded-full pointer-events-none"></div>

      {/* --- HERO SECTION --- */}
      <section className="py-24 text-center relative z-10 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl md:text-8xl font-black text-[#17091a] mb-8 tracking-tighter leading-[1.1]">
            Intelligence <br className="hidden md:block"/>
            <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent px-2">Defined</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-500 max-w-3xl mx-auto font-light leading-relaxed">
            Your trusted partner in turning global complexity into clarity and data into industry-leading innovation.
          </p>
        </div>
      </section>

      {/* --- MISSION STATEMENT --- */}
      <section className="py-20 relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-white/40 backdrop-blur-2xl border border-white/60 rounded-[4rem] p-10 md:p-20 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.05)] overflow-hidden">
            <div className="relative z-10 text-center max-w-4xl mx-auto">
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-[#952877] mb-10">Our Mission</h2>
              <p className="text-2xl md:text-4xl font-black text-[#17091a] leading-tight tracking-tight mb-10">
                In a world of <span className="text-[#952877]">2.5 quintillion bytes</span> of data, we bridge the gap between noise and actionable intelligence.
              </p>
              <div className="h-[2px] w-24 bg-gradient-to-r from-[#952877] to-[#592aa7] mx-auto mb-10"></div>
              <p className="text-lg text-gray-600 font-medium leading-[1.8]">
                We believe that organizations who don't embrace AI will be left behind. Data without intelligence 
                is merely digital noise. Our role is to ensure your organization leads the AI-driven future through strategic engineering.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- NEW SECTION: THE LEADERSHIP PEDIGREE (Based on your content) --- */}
      <section className="py-32 relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-[#952877] mb-6">The Pedigree</h2>
              <h3 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter leading-[1.1] mb-8">
                Engineered by <br/>
                <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent italic px-1">Global Visionaries</span>
              </h3>
              <div className="space-y-6 text-gray-600 font-medium text-lg leading-relaxed">
                <p>
                  Our leadership team brings over <span className="text-[#17091a] font-black">70 years of combined global experience</span> at the forefront of enterprise technology, research, and applied AI.
                </p>
                <p>
                  The founders have built and deployed AI solutions at the world-leading AI organizations behind the most influential large language models in use today.
                </p>
              </div>
            </div>

            <div className="bg-[#17091a] rounded-[3rem] p-8 md:p-12 text-white shadow-2xl relative overflow-hidden group">
              {/* Decorative Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#952877]/20 blur-[80px] rounded-full"></div>
              
              <div className="relative z-10 space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center shrink-0 text-[#952877]">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  </div>
                  <p className="font-light text-gray-300 italic leading-relaxed">
                    "Proven track record of translating advanced AI research into scalable, secure, and commercially successful platforms."
                  </p>
                </div>

                <div className="h-px bg-white/10 w-full"></div>

                <ul className="space-y-6">
                  <li className="flex items-center text-sm font-bold tracking-wide uppercase">
                    <span className="w-2 h-2 rounded-full bg-[#952877] mr-4 shadow-[0_0_10px_#952877]"></span>
                    Ex-Leadership at Global Tech Giants
                  </li>
                  <li className="flex items-center text-sm font-bold tracking-wide uppercase">
                    <span className="w-2 h-2 rounded-full bg-[#952877] mr-4 shadow-[0_0_10px_#952877]"></span>
                    Alumni of Premium Institutions like BITS and IIM.
                  </li>
                  <li className="flex items-center text-sm font-bold tracking-wide uppercase">
                    <span className="w-2 h-2 rounded-full bg-[#952877] mr-4 shadow-[0_0_10px_#952877]"></span>
                    Confidence. Credibility. Outcomes.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- WORLD CLASS EXPERTISE --- */}
      <section className="py-32 relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter">Elite Expertise</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {credentials.map((item, index) => (
              <div 
                key={index}
                className="bg-white/60 backdrop-blur-lg rounded-[2.5rem] p-10 border border-gray-100 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.03)] transition-all duration-500 hover:shadow-[0_40px_80px_-20px_rgba(149,40,119,0.1)] hover:-translate-y-2 group"
              >
                <div className="text-5xl mb-6 grayscale group-hover:grayscale-0 transition-all duration-500 transform group-hover:scale-110 origin-left">
                  {item.icon}
                </div>
                <h3 className="text-xl font-black text-[#17091a] mb-4 uppercase tracking-tight group-hover:text-[#952877] transition-colors">
                  {item.title}
                </h3>
                <p className="text-gray-500 font-medium leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- DIFFERENTIATORS --- */}
      <section className="py-32 bg-[#fafafa] rounded-t-[5rem] relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-xs font-black uppercase tracking-[0.4em] text-[#952877] mb-6">Execution Excellence</h2>
              <h3 className="text-4xl md:text-6xl font-black text-[#17091a] tracking-tighter leading-none">What Sets Us <br className="hidden md:block"/> <span className="italic italic-text">Apart</span></h3>
            </div>
            <p className="text-gray-500 max-w-sm font-medium pb-2">Our unique approach combines deep neural architecture with proven global business strategies.</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10">
            {differentiators.map((item, index) => (
              <div key={index} className="group flex items-center bg-white p-8 rounded-[3rem] border border-gray-100 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1">
                <div className="w-20 h-20 bg-[#fafafa] rounded-3xl flex items-center justify-center flex-shrink-0 group-hover:rotate-6 transition-all duration-500 shadow-inner group-hover:bg-[#17091a]">
                  <img src={item.image} className="w-10 h-10 grayscale group-hover:grayscale-0 group-hover:invert transition-all" alt={item.title} />
                </div>
                <div className="ml-8">
                  <h4 className="text-2xl font-black text-[#17091a] mb-2 group-hover:text-[#952877] transition-colors">{item.title}</h4>
                  <p className="text-gray-500 font-medium text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA SECTION --- */}
      <section className="py-40 text-center relative z-10 px-6 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[#952877]/5 blur-[120px] rounded-full pointer-events-none"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-4xl md:text-6xl font-black text-[#17091a] mb-12 tracking-tighter leading-tight">
            Ready to Lead the <br className="hidden md:block"/>
            <span className="bg-gradient-to-r from-[#952877] to-[#592aa7] bg-clip-text text-transparent  px-2">AI Revolution?</span>
          </h2>
          <a 
            href="/contact" 
            className="inline-flex items-center px-12 py-6 bg-[#17091a] text-white font-black text-xs uppercase tracking-[0.3em] rounded-2xl hover:bg-[#952877] transition-all duration-500 shadow-2xl hover:-translate-y-2"
          >
            Consult Our Experts
            <svg className="w-4 h-4 ml-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;